// Where a practice lands after signing in.
//
// The bug this exists to fix: authorization was always correct — a `customer`
// grant opens the portal and nothing else — but ROUTING was not. Signing in
// dropped everyone on `/`, which is the attorney console: a page headed "Your
// review queue" inside a shell branded "Compliance review", with a
// dispositioned-progress bar measuring a job the practice does not have. A
// customer could reach their own workspace only by knowing the URL.
//
// Nothing here grants or checks permission; that stays per-grant on the
// backend. This decides which door someone is shown first, from the grants
// they already hold:
//
//   exactly one customer grant, nothing else   go straight in
//   several customer grants, nothing else      a workspace picker in their words
//   any attorney or admin capacity             unchanged, the review console
//
// The third case matters and is deliberately conservative: Shawn holds counsel
// on one customer and practice on another, and re-homing him would break the
// surface he actually works in.

import { useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { toApiError, type ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useMyCustomers } from '../hooks/queries';
import { CenterMessage, Spinner } from '../components/ui';
import { Button } from '../ui/primitives';
import { FOOTER_DISCLAIMER } from './display_strings';
import { useSurfaceTheme } from './useSurfaceTheme';
import { isPracticeRole } from '../reviewVocab';

export interface CustomerEntryState {
  /** The list under the signed-in identity is still being read. */
  loading: boolean;
  /** The list could not be read; the gate holds here until a retry succeeds. */
  failed: ApiError | null;
  retry: () => void;
  /** Every capacity this person holds is "the practice": their practices. */
  customerIds: string[] | null;
}

/**
 * Which door, decided from the grants the signed-in identity holds.
 *
 * Three settled states and one unsettled: still reading, could not read,
 * "the practice" (with its workspace ids), or anyone else (the console).
 *
 * "Could not read" is its own state on purpose (BL-596). It used to fall
 * through to "anyone else", so a practice whose list failed was shown the
 * attorney console; and the gate keyed on react-query's `isLoading`, which
 * is pending AND fetching — a query that failed with no data goes back to
 * pending the moment anything refetches it. The console that fall-through
 * mounted holds several readers of this same list, each of whose mount
 * refetched it, which closed the gate, which unmounted them, which on the
 * next failure opened it again: "Loading…" for as long as the endpoint was
 * down. Now nothing below the gate mounts until the list has actually been
 * read, and a failure is shown as a failure, with the retry in the person's
 * hands.
 */
export function useCustomerOnly(skipCustomerList = false): CustomerEntryState {
  const list = useMyCustomers(!skipCustomerList);
  const { isInternalAdmin } = useAuth();
  const { refetch } = list;
  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);
  const elsewhere: CustomerEntryState = {
    loading: false,
    failed: null,
    retry,
    customerIds: null,
  };
  // The team is never "the practice", whatever the list says, so an outage
  // of the list cannot lock an internal admin out of their console.
  if (skipCustomerList || isInternalAdmin) return elsewhere;
  if (list.data === undefined) {
    if (list.isError)
      return { loading: false, failed: toApiError(list.error), retry, customerIds: null };
    return { loading: true, failed: null, retry, customerIds: null };
  }
  const grants = list.data;
  if (grants.length === 0) return elsewhere;
  if (!grants.every((g) => isPracticeRole(g.my_role))) return elsewhere;
  return {
    ...elsewhere,
    customerIds: [...new Set(grants.map((g) => g.customer_id))],
  };
}

export function CustomerEntry() {
  const { data } = useMyCustomers();
  const { customerIds } = useCustomerOnly();
  useSurfaceTheme();

  if (!customerIds) return null;
  if (customerIds.length === 1)
    return <Navigate to={`/portal/${customerIds[0]}`} replace />;

  const rows = (data ?? []).filter(
    (g, i, all) => all.findIndex((x) => x.customer_id === g.customer_id) === i,
  );
  return (
    <div className="vx">
      <div className="vx-container" style={{ paddingBlock: 'var(--vx-13)' }}>
        <h1>Choose a practice</h1>
        <p className="vx-lead" style={{ marginTop: 'var(--vx-5)' }}>
          You have access to {rows.length} practices.
        </p>
        <div
          style={{
            display: 'grid',
            gap: 'var(--vx-6)',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            marginTop: 'var(--vx-9)',
          }}
        >
          {rows.map((c) => (
            <Link
              key={c.customer_id}
              to={`/portal/${c.customer_id}`}
              className="vx-card vx-card--interactive"
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
              }}
            >
              <strong>{c.name}</strong>
              {/* No progress bar and no binder version here. Both are the
                  reviewer's measures of their own work; a practice choosing a
                  workspace has no use for either, and "0% dispositioned" reads
                  to them as a score. */}
            </Link>
          ))}
        </div>
        <p
          className="vx-small"
          style={{ color: 'var(--vx-ink-4)', marginTop: 'var(--vx-12)' }}
        >
          {FOOTER_DISCLAIMER}
        </p>
      </div>
    </div>
  );
}

/** Loading state for the gate, in the customer's visual language. */
export function CustomerEntryLoading() {
  return (
    <CenterMessage>
      <Spinner /> Loading…
    </CenterMessage>
  );
}

function explain(error: ApiError): string {
  if (error.status === 401)
    return 'Your sign-in has expired or is no longer valid. Sign out, then sign in again.';
  if (error.status === 403)
    return 'We couldn’t find a practice account for this sign-in. Open your invitation link, or reply to the invitation for help.';
  if (error.status === 0)
    return 'VerityRx could not be reached. Check your connection, then try again.';
  return 'Try again. If it still fails, contact your VerityRx team.';
}

/**
 * The gate's failure state: the list of what this person may open could not
 * be read, so nothing is opened. Says what happened, offers the retry, and
 * offers the way out — a sign-out — for the case where the sign-in itself is
 * what failed. Routes nowhere on its own.
 */
export function CustomerEntryFailed({
  error,
  retry,
}: {
  error: ApiError;
  retry: () => void;
}) {
  const { logout } = useAuth();
  return (
    <div className="vx">
      <div
        className="vx-container"
        style={{ paddingBlock: 'var(--vx-13)' }}
        role="alert"
        data-testid="entry-failed"
      >
        <h1>We couldn’t load your practices</h1>
        <p className="vx-lead" style={{ marginTop: 'var(--vx-5)' }}>
          {explain(error)}
        </p>
        {error.detail && (
          <p
            className="vx-small"
            style={{ color: 'var(--vx-ink-4)', marginTop: 'var(--vx-3)' }}
          >
            The server said: {error.detail}
          </p>
        )}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--vx-4)',
            marginTop: 'var(--vx-8)',
          }}
        >
          <Button variant="primary" onClick={retry}>
            Try again
          </Button>
          <Button onClick={logout}>Sign out</Button>
        </div>
      </div>
    </div>
  );
}
